const BASE_URL = 'https://www.tutorfounder.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = 'Tutor Founder';

export interface PageSEO {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
}

export function buildPageSEO(
  title: string,
  description: string,
  path: string,
  ogImage?: string,
): PageSEO {
  return {
    title: `${title} | ${SITE_NAME}`,
    description,
    canonical: `${BASE_URL}${path}`,
    ogImage: ogImage ?? DEFAULT_IMAGE,
  };
}

/** JSON-LD: Organization */
export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: BASE_URL,
    logo: `${BASE_URL}/favicon-1024.png`,
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      availableLanguage: ['English', 'Malay'],
    },
  };
}

/** JSON-LD: LocalBusiness (Educational) */
export function localBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: SITE_NAME,
    url: BASE_URL,
    description:
      "Malaysia's tutor matching platform connecting parents with qualified, verified tutors for primary, secondary, and international school subjects.",
    areaServed: {
      '@type': 'Country',
      name: 'Malaysia',
    },
    serviceType: 'Tutor Matching Platform',
    priceRange: 'RM30 – RM150 per hour',
  };
}

/** JSON-LD: FAQPage */
export function faqPageSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/** JSON-LD: BreadcrumbList */
export function breadcrumbSchema(crumbs: Array<{ name: string; path: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: `${BASE_URL}${crumb.path}`,
    })),
  };
}

/** JSON-LD: Tutor profile (Person + EducationalOccupationalCredential) */
export function tutorProfileSchema(tutor: {
  id: string;
  fullName: string;
  subjects: string[];
  areas: string[];
  hourlyRate: string;
  verified: boolean;
  rating?: number;
  yearsOfExperience?: number;
  profilePicture?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: tutor.fullName,
    url: `${BASE_URL}/tutor/${tutor.id}`,
    image: tutor.profilePicture ?? undefined,
    jobTitle: 'Tutor',
    knowsAbout: tutor.subjects,
    areaServed: tutor.areas,
    ...(tutor.verified && {
      hasCredential: {
        '@type': 'EducationalOccupationalCredential',
        name: 'Tutor Founder Verified',
      },
    }),
    ...(tutor.rating !== undefined && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: tutor.rating,
        bestRating: 5,
        worstRating: 1,
      },
    }),
    offers: {
      '@type': 'Offer',
      price: tutor.hourlyRate,
      priceCurrency: 'MYR',
      availability: 'https://schema.org/InStock',
    },
  };
}
